import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from './useDebounce';
import { usePagination } from './usePagination';
import { useColumnFilters } from './useColumnFilters';
import type { TableColumn, ColumnFilters, ColumnFilterValue } from '@app-types';

interface UseSchedulePageStateOptions {
  defaultFilter?: string;
  defaultSort?: string;
  defaultSortOrder?: 'ASC' | 'DESC';
  defaultCheckboxFilter?: boolean;
  columnSortMapping: Record<string, string>;
  columns: TableColumn[];
  columnStorageKey: string;
}

export interface UseSchedulePageStateReturn {
  // Search
  searchQuery: string;
  debouncedSearchQuery: string;
  handleSearch: (value: string) => void;
  
  // Filter
  filter: string;
  handleFilterChange: (filter: string) => void;
  
  // Checkbox
  checkboxFilter: boolean;
  handleCheckboxChange: (checked: boolean) => void;
  
  // Pagination
  currentPage: number;
  pageSize: number;
  setCurrentPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  
  // Sorting
  sortBy: string;
  sortOrder: string;
  handleColumnSort: (columnKey: string) => void;
  getSortIndicator: (columnKey: string) => string | null;
  isColumnSorted: (columnKey: string) => boolean;
  
  // Columns
  columns: TableColumn[];
  visibleColumns: Set<string>;
  renderedColumns: TableColumn[];
  toggleColumn: (columnKey: string) => void;

  // Column Filters
  columnFilterValues: ColumnFilters;
  debouncedColumnFilters: ColumnFilters;
  setColumnFilter: (columnKey: string, value: ColumnFilterValue | null) => void;
  clearColumnFilters: () => void;
  hasActiveColumnFilters: boolean;
  getColumnFiltersParam: () => string | null;
}

/**
 * Generic hook for managing schedule page state (Scheduler & Assessor)
 * Handles: search, filters, pagination, sorting, column visibility, URL sync
 */
export function useSchedulePageState(options: UseSchedulePageStateOptions): UseSchedulePageStateReturn {
  const {
    defaultFilter = 'all',
    defaultSort = 'created_at',
    defaultSortOrder = 'ASC',
    defaultCheckboxFilter = false,
    columnSortMapping,
    columns,
    columnStorageKey
  } = options;

  const [searchParams, setSearchParams] = useSearchParams();

  // Search state
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Filter state
  const [filter, setFilter] = useState(searchParams.get('state') || searchParams.get('status') || defaultFilter);
  
  // Checkbox filter (processedByMe/myMeetingsOnly)
  const [checkboxFilter, setCheckboxFilter] = useState(() => {
    const urlParam = searchParams.get('processedByMe') || searchParams.get('myMeetings');
    if (urlParam !== null) {
      return urlParam === 'true';
    }
    return defaultCheckboxFilter;
  });

  // Pagination with localStorage persistence
  const { 
    currentPage, 
    pageSize, 
    setCurrentPage, 
    setPageSize,
    resetToFirstPage 
  } = usePagination({
    storageKey: columnStorageKey.replace('-visible-columns', '-page-size'), // Reuse storage key pattern
    defaultPageSize: 25
  });

  // Column filters with URL persistence and debounce
  const {
    filterValues: columnFilterValues,
    debouncedFilterValues: debouncedColumnFilters,
    setFilter: setColumnFilter,
    clearAllFilters: clearColumnFilters,
    hasActiveFilters: hasActiveColumnFilters,
    getApiParam: getColumnFiltersParam
  } = useColumnFilters({ columns });

  // Sorting state
  const [sortBy, setSortBy] = useState(defaultSort);
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>(defaultSortOrder);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    const stored = localStorage.getItem(columnStorageKey);
    if (stored) {
      return new Set(JSON.parse(stored));
    }
    return new Set(columns.filter(col => col.defaultVisible).map(col => col.key));
  });

  // Save column preferences to localStorage
  useEffect(() => {
    localStorage.setItem(columnStorageKey, JSON.stringify(Array.from(visibleColumns)));
  }, [visibleColumns, columnStorageKey]);

  // Update URL when filters change (using debounced search query)
  // Note: Pagination (page, limit) is handled by usePagination hook
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams); // Preserve existing params (page, limit)
    
    if (debouncedSearchQuery) {
      newParams.set('search', debouncedSearchQuery);
    } else {
      newParams.delete('search');
    }
    
    // Use appropriate param name for filter (state or status)
    const filterParam = searchParams.get('state') !== null ? 'state' : 'status';
    if (filter && filter !== defaultFilter) {
      newParams.set(filterParam, filter);
    } else {
      newParams.delete(filterParam);
    }
    
    // Use appropriate param name for checkbox (processedByMe or myMeetings)
    // Always set the value explicitly (even if false) to preserve user preference on reload
    const checkboxParam = searchParams.get('processedByMe') !== null ? 'processedByMe' : 'myMeetings';
    newParams.set(checkboxParam, checkboxFilter.toString());
    
    setSearchParams(newParams, { replace: true });
  }, [debouncedSearchQuery, filter, checkboxFilter, setSearchParams, defaultFilter, searchParams]);

  // Handlers with page reset
  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    resetToFirstPage();
  }, [resetToFirstPage]);

  const handleFilterChange = useCallback((newFilter: string) => {
    setFilter(newFilter);
    resetToFirstPage();
  }, [resetToFirstPage]);

  const handleCheckboxChange = useCallback((checked: boolean) => {
    setCheckboxFilter(checked);
    resetToFirstPage();
  }, [resetToFirstPage]);

  // Sorting handlers
  const handleColumnSort = useCallback((columnKey: string) => {
    const backendField = columnSortMapping[columnKey];
    if (!backendField) return;

    if (sortBy === backendField) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(backendField);
      // Default order for date fields is DESC, otherwise ASC
      const isDateField = ['created_at', 'updated_at', 'start_date', 'end_date', 'scheduled_datetime'].includes(backendField);
      setSortOrder(isDateField ? 'DESC' : 'ASC');
    }
    resetToFirstPage();
  }, [sortBy, sortOrder, columnSortMapping, resetToFirstPage]);

  const getSortIndicator = useCallback((columnKey: string) => {
    const backendField = columnSortMapping[columnKey];
    if (sortBy !== backendField) return null;
    
    return sortOrder === 'ASC' ? '↑' : '↓';
  }, [sortBy, sortOrder, columnSortMapping]);

  const isColumnSorted = useCallback((columnKey: string) => {
    const backendField = columnSortMapping[columnKey];
    return sortBy === backendField;
  }, [sortBy, columnSortMapping]);

  // Column visibility toggle
  const toggleColumn = useCallback((columnKey: string) => {
    const column = columns.find(col => col.key === columnKey);
    if (column?.alwaysVisible) return;

    setVisibleColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnKey)) {
        newSet.delete(columnKey);
      } else {
        newSet.add(columnKey);
      }
      return newSet;
    });
  }, [columns]);

  // Filtered columns based on visibility
  const renderedColumns = useMemo(() => {
    return columns.filter(col => visibleColumns.has(col.key));
  }, [columns, visibleColumns]);

  return {
    // Search
    searchQuery,
    debouncedSearchQuery,
    handleSearch,
    
    // Filter
    filter,
    handleFilterChange,
    
    // Checkbox
    checkboxFilter,
    handleCheckboxChange,
    
    // Pagination
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
    
    // Sorting
    sortBy,
    sortOrder,
    handleColumnSort,
    getSortIndicator,
    isColumnSorted,
    
    // Columns
    columns,
    visibleColumns,
    renderedColumns,
    toggleColumn,

    // Column Filters
    columnFilterValues,
    debouncedColumnFilters,
    setColumnFilter,
    clearColumnFilters,
    hasActiveColumnFilters,
    getColumnFiltersParam,
  };
}

